import { AlertCircle, Bell, Clock, RotateCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function CasesViewSkeleton() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2.5 text-ink-tertiary text-[0.9375rem]">
        <RotateCw size={16} className="text-accent animate-loader-spin" />
        Cargando tus casos…
      </div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[88px] rounded-lg bg-surface animate-loader-pulse"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export function CasesViewError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center text-center gap-2.5 py-18 px-4">
      <div className="w-13 h-13 rounded-full bg-destructive-subtle flex items-center justify-center mb-1.5">
        <AlertCircle size={24} className="text-destructive" />
      </div>
      <p className="m-0 text-[1.125rem] font-semibold text-ink">No pudimos cargar tus casos</p>
      <p className="m-0 text-[0.9375rem] text-ink-tertiary max-w-[340px]">
        Revisa tu conexión a internet e intenta de nuevo. Tus datos están guardados.
      </p>
      <button
        onClick={onRetry}
        className="mt-3 px-5.5 py-3 min-h-11 bg-accent text-ink-inverse border-none rounded-sm text-[0.9375rem] font-semibold cursor-pointer hover:bg-accent-hover transition-colors duration-150"
      >
        Intentar de nuevo
      </button>
    </div>
  );
}

export function CasesViewEmpty() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center text-center gap-2.5 py-18 px-4">
      <div className="w-14 h-14 rounded-full bg-accent-subtle border border-accent/25 flex items-center justify-center mb-1.5">
        <Bell size={26} className="text-accent" />
      </div>
      <p className="m-0 text-[1.125rem] font-semibold text-ink">Aún no sigues ningún caso</p>
      <p className="m-0 text-[0.9375rem] text-ink-tertiary max-w-[360px]">
        Agrega un número de radicado y te avisaremos por correo cuando haya movimientos.
      </p>
      <button
        onClick={() => navigate('/busquedas')}
        className="mt-3 px-5.5 py-3 min-h-11 bg-accent text-ink-inverse border-none rounded-sm text-[0.9375rem] font-semibold cursor-pointer hover:bg-accent-hover transition-colors duration-150"
      >
        Agregar mi primer caso
      </button>
    </div>
  );
}

interface CasesViewPreparingProps {
  count: number;
}

export function CasesViewPreparing({ count }: CasesViewPreparingProps) {
  return (
    <div className="flex flex-col items-center text-center gap-2.5 py-18 px-4">
      <div className="w-14 h-14 rounded-full bg-accent-subtle border border-accent/25 flex items-center justify-center mb-1.5">
        <Clock size={26} className="text-accent" />
      </div>
      <p className="m-0 text-[1.125rem] font-semibold text-ink">
        Tus casos están listos para consultarse
      </p>
      <p className="m-0 text-[0.9375rem] text-ink-tertiary max-w-[360px]">
        {count === 1
          ? 'Tienes 1 búsqueda configurada. Aún no hemos hecho la primera consulta.'
          : `Tienes ${count} búsquedas configuradas. Aún no hemos hecho la primera consulta.`}
      </p>
      <p className="m-0 text-[0.8125rem] text-ink-muted">Al terminar verás aquí tus casos.</p>
    </div>
  );
}
